/* Napurel buy box behaviour: fit check, tabs, sticky add to cart, variant-change sync.
   Relies on Dawn globals: subscribe(), PUB_SUB_EVENTS (pubsub.js / constants.js). */

(function () {
  function getSelectedSize(sectionId, sizeOptionName) {
    const picker = document.querySelector(`#variant-selects-${sectionId} [data-pp-size-picker]`);
    if (!picker) return null;
    const checked = picker.querySelector('input:checked');
    return checked ? checked.value : null;
  }

  /* ---------- Fit check ---------- */
  if (!customElements.get('pp-fit-check')) {
    class PpFitCheck extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        try {
          this.data = JSON.parse(this.querySelector('[data-pp-fit-data]').textContent);
        } catch (e) {
          this.data = { sizes: {}, labels: [] };
        }
        this.depth = null;
        this.buttons = Array.from(this.querySelectorAll('.pp-fit__depth'));
        this.result = this.querySelector('[data-pp-fit-result]');
        this.text = this.querySelector('[data-pp-fit-text]');
        this.badge = this.querySelector('[data-pp-fit-badge]');

        this.buttons.forEach((btn) => {
          btn.addEventListener('click', () => this.select(Number(btn.dataset.depth)));
          btn.addEventListener('keydown', (event) => this.onKey(event, btn));
        });
        this.setRoving();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId === this.sectionId) this.render();
          });
        }
        // Size radios change before the section re-render finishes; update immediately as well.
        this.onChange = (event) => {
          if (event.target.closest && event.target.closest('[data-pp-size-picker]')) {
            requestAnimationFrame(() => this.render());
          }
        };
        document.addEventListener('change', this.onChange);
        this.render();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
        document.removeEventListener('change', this.onChange);
      }

      setRoving() {
        const active = this.buttons.find((b) => b.getAttribute('aria-checked') === 'true') || this.buttons[0];
        this.buttons.forEach((b) => (b.tabIndex = b === active ? 0 : -1));
      }

      onKey(event, btn) {
        const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const i = this.buttons.indexOf(btn);
        const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = this.buttons[(i + step + this.buttons.length) % this.buttons.length];
        next.focus();
        this.select(Number(next.dataset.depth));
      }

      select(depth) {
        this.depth = depth;
        this.buttons.forEach((b) => b.setAttribute('aria-checked', String(Number(b.dataset.depth) === depth)));
        this.setRoving();
        this.render();
        this.dispatchEvent(new CustomEvent('pp:fit-check', { bubbles: true, detail: { depth } }));
      }

      render() {
        const size = getSelectedSize(this.sectionId);
        const sizeData = size ? this.data.sizes[size] : null;

        if (this.badge) {
          const tag = sizeData ? (sizeData.sample ? 'SAMPLE' : this.data.measuredLabel || '') : '';
          this.badge.textContent = size ? (tag ? `${size} · ${tag}` : size) : '';
        }

        if (!size || !sizeData) {
          this.result.dataset.state = 'empty';
          this.text.textContent = size ? this.data.emptyText : this.data.noSizeText;
          return;
        }
        if (!this.depth) {
          this.result.dataset.state = 'empty';
          this.text.textContent = this.data.emptyText;
          return;
        }
        const state = this.depth <= 2 ? 'good' : this.depth === 3 ? 'snug' : 'no';
        this.result.dataset.state = state;
        this.text.innerHTML = '';
        const strong = document.createElement('strong');
        strong.textContent = this.data.labels[this.depth] || '';
        this.text.append(strong, document.createTextNode(' ' + (sizeData.results[this.depth] || '')));
      }
    }
    customElements.define('pp-fit-check', PpFitCheck);
  }

  /* ---------- Tabs ---------- */
  if (!customElements.get('pp-tabs')) {
    class PpTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.activate(tab));
          tab.addEventListener('keydown', (event) => {
            const i = this.tabs.indexOf(tab);
            let next = null;
            if (event.key === 'ArrowRight') next = this.tabs[(i + 1) % this.tabs.length];
            if (event.key === 'ArrowLeft') next = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
            if (event.key === 'Home') next = this.tabs[0];
            if (event.key === 'End') next = this.tabs[this.tabs.length - 1];
            if (next) {
              event.preventDefault();
              next.focus();
              this.activate(next);
            }
          });
        });
      }

      activate(tab) {
        this.tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          const panel = this.querySelector(`#${t.getAttribute('aria-controls')}`);
          if (panel) panel.hidden = !selected;
        });
      }
    }
    customElements.define('pp-tabs', PpTabs);
  }

  /* ---------- Benefit bullets: "Show N more" on mobile ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.pp-bullets__toggle');
    if (!toggle) return;
    const wrap = toggle.closest('.pp-bullets-wrap');
    const open = !wrap.classList.contains('is-open');
    wrap.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.querySelector('span').textContent = open ? toggle.dataset.less : toggle.dataset.more;
  });

  /* ---------- Featured reviews: horizontal slider with dots, arrows, swipe and gentle autoplay ---------- */
  if (!customElements.get('pp-review-slider')) {
    class PpReviewSlider extends HTMLElement {
      connectedCallback() {
        clearInterval(this.timer);
        if (this.initialized) {
          this.startAutoplay();
          return;
        }
        this.initialized = true;
        this.track = this.querySelector('[data-pp-track]');
        this.slides = Array.from(this.querySelectorAll('[data-pp-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-pp-dot]'));
        this.index = 0;
        if (this.slides.length < 2) return;
        this.slides[0].classList.add('is-active');
        this.classList.add('is-ready');

        this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.ppDot), true)));
        this.querySelector('[data-pp-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
        this.querySelector('[data-pp-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
        this.track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.go(this.index + 1, true);
          if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
        });
        this.track.addEventListener(
          'scroll',
          () => {
            cancelAnimationFrame(this.frame);
            this.frame = requestAnimationFrame(() => {
              if (Date.now() < this.programmaticUntil) return;
              const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
              if (i !== this.index) this.setActive(i);
            });
          },
          { passive: true }
        );

        // Autoplay: only while visible and not being touched, hovered or focused.
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.interval = Number(this.dataset.interval) || 5000;
        this.paused = false;
        if (this.dataset.autoplay === 'true' && !reduce && !window.Shopify?.designMode) {
          const pause = () => (this.paused = true);
          const resume = () => (this.paused = false);
          this.addEventListener('pointerenter', pause);
          this.addEventListener('pointerleave', resume);
          this.addEventListener('focusin', pause);
          this.addEventListener('focusout', resume);
          this.track.addEventListener('touchstart', () => {
            pause();
            clearTimeout(this.resumeTimer);
            this.resumeTimer = setTimeout(resume, 8000);
          }, { passive: true });
          this.visible = false;
          if ('IntersectionObserver' in window) {
            new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting), { threshold: 0.6 }).observe(this);
          }
          this.autoplay = true;
          this.startAutoplay();
        }
      }

      startAutoplay() {
        clearInterval(this.timer);
        if (!this.autoplay) return;
        this.timer = setInterval(() => {
          if (this.visible && !this.paused && !document.hidden) this.go(this.index + 1);
        }, this.interval);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        clearTimeout(this.resumeTimer);
      }

      go(i, user) {
        const count = this.slides.length;
        const next = (i + count) % count;
        if (user) {
          this.paused = true;
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(() => (this.paused = false), 8000);
        }
        this.programmaticUntil = Date.now() + 700;
        this.track.scrollTo({ left: next * this.track.clientWidth });
        this.setActive(next);
      }

      setActive(i) {
        this.index = i;
        this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
        this.dots.forEach((dot, n) => {
          dot.classList.toggle('is-active', n === i);
          if (n === i) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      }
    }
    customElements.define('pp-review-slider', PpReviewSlider);
  }

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('pp-sticky-atc')) {
    class PpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.button = this.querySelector('.pp-sticky__btn');
        this.pastMain = false;
        this.footerIn = false;
        this.cartOpen = false;

        this.button.addEventListener('click', () => {
          if (this.button.dataset.mode === 'scroll') {
            const box = document.getElementById('pp-buybox') || document.querySelector('product-info');
            if (box) {
              const top = box.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
            }
            return;
          }
          const main = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          if (main && !main.disabled) main.click();
        });

        if ('IntersectionObserver' in window) {
          const main = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
          if (main) {
            this.observer = new IntersectionObserver(([entry]) => {
              this.pastMain = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.update();
            });
            this.observer.observe(main);
          }
          const footer = document.querySelector('.footer, footer');
          if (footer) {
            this.footObserver = new IntersectionObserver(([entry]) => {
              this.footerIn = entry.isIntersecting;
              this.update();
            });
            this.footObserver.observe(footer);
          }
        }

        this.onCart = (event) => {
          this.cartOpen = event.detail.open;
          this.update();
        };
        document.addEventListener('pp:cart-toggle', this.onCart);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.footObserver) this.footObserver.disconnect();
        document.removeEventListener('pp:cart-toggle', this.onCart);
      }

      update() {
        const show = this.pastMain && !this.footerIn && !this.cartOpen;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
        this.button.tabIndex = show ? 0 : -1;
      }

      sync(html) {
        ['pp-sticky-meta', 'pp-sticky-media'].forEach((id) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const target = document.getElementById(`${id}-${this.sectionId}`);
          if (source && target) target.innerHTML = source.innerHTML;
        });
        const sourceBtn = html.getElementById(`pp-sticky-btn-${this.sectionId}`);
        if (sourceBtn) {
          this.button.dataset.mode = sourceBtn.dataset.mode;
          this.button.textContent = sourceBtn.textContent.trim();
        }
      }
    }
    customElements.define('pp-sticky-atc', PpStickyAtc);
  }

  /* ---------- Add to cart label: price × quantity, Adding… / Added to cart. ---------- */
  function suffixFor(sectionId) {
    return document.getElementById(`pp-atc-suffix-${sectionId}`);
  }

  function updateSuffix(sectionId) {
    const suffix = suffixFor(sectionId);
    const qtyInput = document.getElementById(`Quantity-${sectionId}`);
    if (!suffix) return;
    let prices = {};
    try {
      prices = JSON.parse(suffix.dataset.prices || '{}');
    } catch (e) {}
    const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
    if (prices[qty]) suffix.textContent = `· ${prices[qty]}`;
  }

  function setLabel(sectionId, text) {
    const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (!button) return;
    const label = button.querySelector('span');
    const suffix = suffixFor(sectionId);
    if (!button.dataset.ppLabel) button.dataset.ppLabel = label.textContent.trim();
    if (text) {
      label.textContent = text;
      if (suffix) suffix.classList.add('pp-hide');
    } else {
      label.textContent = button.dataset.ppLabel;
      if (suffix) suffix.classList.remove('pp-hide');
    }
  }

  function initBuyRow(buy) {
    const sectionId = buy.dataset.section;
    updateSuffix(sectionId);
    buy.addEventListener('change', (event) => {
      if (event.target.matches('.quantity__input')) updateSuffix(sectionId);
    });
    buy.addEventListener('click', (event) => {
      if (event.target.closest('.quantity__button')) requestAnimationFrame(() => updateSuffix(sectionId));
    });
  }

  /* Max sets per order + "Adding…" label: runs before product-form.js handles the submit */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const productForm = form.closest && form.closest('product-form');
      if (!productForm) return;
      const sectionId = productForm.dataset.sectionId;
      const buy = document.querySelector(`[data-pp-buy][data-section="${sectionId}"]`);
      if (!buy) return;
      const qtyInput = document.getElementById(`Quantity-${sectionId}`);
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      const drawer = document.querySelector('pp-cart-drawer');
      if (drawer && !drawer.canAdd(qty)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        drawer.open();
        drawer.showError(drawer.dataset.maxError);
        return;
      }
      setLabel(sectionId, buy.dataset.adding);
    },
    true
  );

  /* ---------- Variant-change sync for pp- elements outside Dawn's update list ---------- */
  function syncFromHtml(sectionId, html) {
    const suffixSource = html.getElementById(`pp-atc-suffix-${sectionId}`);
    const suffixTarget = suffixFor(sectionId);
    if (suffixSource && suffixTarget) {
      suffixTarget.dataset.prices = suffixSource.dataset.prices || '{}';
      suffixTarget.classList.toggle('hidden', suffixSource.classList.contains('hidden'));
      updateSuffix(sectionId);
    }
    [`pp-chips-${sectionId}`, `pp-soldout-${sectionId}`].forEach((id) => {
      const source = html.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) {
        target.innerHTML = source.innerHTML;
        target.hidden = source.hidden;
      }
    });
    const button = document.getElementById(`ProductSubmitButton-${sectionId}`);
    if (button) delete button.dataset.ppLabel;
    document.querySelectorAll(`pp-sticky-atc[data-section="${sectionId}"]`).forEach((el) => el.sync(html));
  }

  function init() {
    document.querySelectorAll('[data-pp-buy]').forEach(initBuyRow);
    applyDefaultSize();
    initSizeChart();
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const { sectionId, html } = event.data;
      if (html) syncFromHtml(sectionId, html);
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source !== 'product-form') return;
      document.querySelectorAll('[data-pp-buy]').forEach((buy) => {
        const sectionId = buy.dataset.section;
        setLabel(sectionId, buy.dataset.added);
        const qtyInput = document.getElementById(`Quantity-${sectionId}`);
        if (qtyInput) {
          qtyInput.value = 1;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        setTimeout(() => setLabel(sectionId, ''), 1800);
      });
    });
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      document.querySelectorAll('[data-pp-buy]').forEach((buy) => setLabel(buy.dataset.section, ''));
    });
  }

  /* Default size (picker block setting): when the link carries no ?variant=, pick that size once,
     through Dawn's own variant-selects change flow so price, ATC, gallery and URL all follow. */
  /* Size chart popup (Check your fit): clones the Size & fit section's size tabs + spec list and the fit guide,
     so there is one source of truth. Size buttons inside keep working through pp-sections.js (they pick the size). */
  function initSizeChart() {
    const source = document.querySelector('#pp-size-fit .pp-sizefit__m[data-pp-size-table]');
    document.querySelectorAll('[data-pp-sizechart-open]').forEach((btn) => {
      const fit = btn.closest('pp-fit-check');
      const dialog = fit && fit.querySelector('[data-pp-sizechart]');
      if (!source || !dialog || typeof dialog.showModal !== 'function') return;
      btn.hidden = false;
      const body = dialog.querySelector('[data-pp-sizechart-body]');
      const close = () => {
        if (!dialog.open) return;
        dialog.classList.add('is-closing');
        setTimeout(() => {
          dialog.classList.remove('is-closing');
          dialog.close();
        }, 200);
      };
      btn.addEventListener('click', () => {
        if (!body.childElementCount) {
          const table = source.cloneNode(true);
          table.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          body.appendChild(table);
          const rule = document.querySelector('#pp-size-fit .pp-sizefit__rule');
          if (rule) body.appendChild(rule.cloneNode(true));
        } else {
          // Re-sync the selected size from the live table
          const live = source.querySelectorAll('[data-size], [data-pp-pick-size]');
          const copy = body.querySelectorAll('[data-size], [data-pp-pick-size]');
          live.forEach((el, i) => {
            if (!copy[i]) return;
            copy[i].classList.toggle('is-selected', el.classList.contains('is-selected'));
            if (el.hasAttribute('aria-pressed')) copy[i].setAttribute('aria-pressed', el.getAttribute('aria-pressed'));
          });
        }
        dialog.showModal();
        document.documentElement.classList.add('pp-sizechart-open');
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('pp-sizechart-open');
        btn.focus({ preventScroll: true });
      });
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        close();
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-pp-sizechart-close]')) close();
      });
      // Swipe the sheet down to close (phones)
      const sheet = dialog.querySelector('.pp-sizechart__sheet');
      let startY = null;
      sheet.addEventListener('touchstart', (e) => {
        startY = body.scrollTop <= 0 ? e.touches[0].clientY : null;
      }, { passive: true });
      sheet.addEventListener('touchmove', (e) => {
        if (startY === null) return;
        const dy = Math.max(0, e.touches[0].clientY - startY);
        sheet.style.transform = dy ? `translateY(${dy}px)` : '';
      }, { passive: true });
      sheet.addEventListener('touchend', (e) => {
        if (startY === null) return;
        const dy = e.changedTouches[0].clientY - startY;
        sheet.style.transform = '';
        startY = null;
        if (dy > 90) close();
      });
    });
  }

  function applyDefaultSize() {
    const picker = document.querySelector('[data-pp-default-size]');
    if (!picker || new URLSearchParams(window.location.search).has('variant')) return;
    const wanted = picker.dataset.ppDefaultSize.trim().toLowerCase();
    const input = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
      (el) => el.value.trim().toLowerCase() === wanted
    );
    if (!input || input.checked || input.disabled || input.classList.contains('disabled')) return;
    const run = () => {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      // Dawn re-focuses the changed input after the re-render; drop that focus on page load
      setTimeout(() => {
        const active = document.activeElement;
        if (active && active.matches && active.matches('[data-pp-size-picker] input')) active.blur();
      }, 900);
    };
    if (window.customElements && !customElements.get('variant-selects')) {
      customElements.whenDefined('variant-selects').then(run);
    } else {
      run();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
